/* ---------------------------------
Event Normalization Utilities
--------------------------------- */

import { EventType } from '@/types/EventType'
import { BuonInvitoEventType } from '@/types/BuonInvitoEventType'
import { NormalizedEventType } from '@/types/NormalizedEventType'
import { formatDate } from './date'
import DOMPurify from 'isomorphic-dompurify'

export enum EventTypeEnum {
  REGULAR = 'REGULAR',
  BUON_INVITO = 'BUON_INVITO',
}

type SupportedEventTypes = EventType | BuonInvitoEventType

export function normalizeEvent(
  event: SupportedEventTypes,
  eventType: EventTypeEnum
): NormalizedEventType {
  switch (eventType) {
    // REGULAR EVENT
    case EventTypeEnum.REGULAR: {
      const {
        title,
        subtitle,
        pagedata,
        eventstart,
        eventend,
        regions,
        location,
        featuredimage,
        url,
      } = (event as EventType) ?? {}

      // TODO clarify which is the correct description
      const _description =
        subtitle || pagedata?.ctadata?.description || pagedata?.ctadata?.excerpt

      return {
        title,
        description: DOMPurify.sanitize(_description ?? '', {
          USE_PROFILES: { html: false },
        }),
        startDate: formatDate(eventstart),
        endDate: formatDate(eventend),
        region: regions?.[0] ?? '…',
        location,
        imageUrl: computeURL(featuredimage?.url),
        imageAlt:
          featuredimage?.alt ||
          `Immagine rappresentativa dell'evento "${title}"`,
        ctaLabel: pagedata?.ctadata?.['cta-label'] || 'Più informazioni',
        // This is meant to be the URL to the event's page,
        // not strictly the event's CTA URL.
        // It's called ctaUrl because it
        // will populate a CTA on our frontend
        ctaUrl: url ? process.env.MAIN_WEBSITE_URL + url : undefined,
        // …EventType doesn't have tags
      }
    }

    // "BUON INVITO" EVENT
    case EventTypeEnum.BUON_INVITO: {
      const {
        title,
        pagedata,
        begindate,
        deadline,
        image,
        featuredimage,
        url,
        tags,
      } = (event as BuonInvitoEventType) ?? {}

      // These two fields seem to hold the same value
      const _url = url || pagedata?.ctadata?.['action-url']

      return {
        title,
        // description seems to be invitee name
        description: DOMPurify.sanitize(pagedata?.ctadata?.excerpt ?? '', {
          USE_PROFILES: { html: false },
        }),
        startDate: formatDate(begindate),
        endDate: formatDate(deadline),
        // BuonInvitoEventType doesn't have region
        region: undefined,
        // BuonInvitoEventType doesn't have location
        location: undefined,
        // These two fields seem to hold the same value
        imageUrl: computeURL(image?.url || featuredimage?.url),
        // This data type doesn't have imageAlt
        imageAlt: `Immagine rappresentativa dell'evento "${title}"`,
        ctaLabel: pagedata?.ctadata?.['cta-label'],
        ctaUrl: _url ? process.env.BUONINVITO_WEBSITE_URL + _url : undefined,
        tags: tags,
      }
    }

    default:
      throw new Error(`Unsupported event type: ${eventType}`)
  }
}

function computeURL(url: string | undefined) {
  // `url` can be smthg like: "/uploads/<FILENAME>.jpeg"
  // or a fully-qualified URL to the resource.
  if (!url) return undefined

  if (/^http(s)?:\/\//.test(url)) {
    // This value comes as a fully-qualified URL,
    // so the CMS sets it and there's no need to
    // manipulate it or construct it.
    return url
  }

  // This value comes as a URL pathname only,
  // like: "/uploads/img.jpeg"
  return process.env.MAIN_CDN_URL + url
}
